const removeControlCharacters = (value) =>
  Array.from(value)
    .filter((character) => {
      const codePoint = character.codePointAt(0);
      return codePoint > 31 && codePoint !== 127;
    })
    .join("");
const INVALID_FILENAME_CHARS = /[\\/:*?"<>|]+/g;
const INVALID_EXTENSION_CHARS = /[^a-zA-Z0-9]+/g;
const MAX_FILENAME_LENGTH = 140;

const normalizeString = (value) =>
  typeof value === "string" ? value.normalize("NFKC") : String(value ?? "");

const sanitizeBaseName = (value, fallback = "arquivo") => {
  const normalized = removeControlCharacters(normalizeString(value))
    .replace(INVALID_FILENAME_CHARS, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (!normalized) {
    return fallback;
  }
  if (normalized.length > MAX_FILENAME_LENGTH) {
    return normalized.slice(0, MAX_FILENAME_LENGTH).trim();
  }
  return normalized;
};

const sanitizeExtension = (value) => {
  const normalized = removeControlCharacters(normalizeString(value))
    .replace(INVALID_EXTENSION_CHARS, "")
    .trim();
  return normalized.slice(0, 12).toLowerCase();
};

const splitFileName = (value) => {
  const stringValue = normalizeString(value);
  const lastDot = stringValue.lastIndexOf(".");
  if (lastDot <= 0 || lastDot === stringValue.length - 1) {
    return { base: stringValue, extension: "" };
  }
  const base = stringValue.slice(0, lastDot);
  const extension = stringValue.slice(lastDot + 1);
  return { base, extension };
};

const formatDateValue = (value) => {
  if (!value) return "";
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return "";
    const day = String(value.getDate()).padStart(2, "0");
    const month = String(value.getMonth() + 1).padStart(2, "0");
    const year = String(value.getFullYear());
    return `${day}-${month}-${year}`;
  }
  if (typeof value === "string") {
    const isoDateOnly = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (isoDateOnly) {
      const [, year, month, day] = isoDateOnly;
      return `${day}-${month}-${year}`;
    }
  }
  const parsed = new Date(value);
  if (!Number.isNaN(parsed.getTime())) {
    return formatDateValue(parsed);
  }
  if (typeof value === "string" && /^(\d{2})[-/.](\d{2})[-/.](\d{4})$/.test(value)) {
    const match = value.match(/^(\d{2})[-/.](\d{2})[-/.](\d{4})$/);
    if (match) {
      const [, day, month, year] = match;
      return `${day}-${month}-${year}`;
    }
  }
  return "";
};

const formatTimeValue = (value) => {
  if (!value) return "";
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return "";
    const hours = String(value.getHours()).padStart(2, "0");
    const minutes = String(value.getMinutes()).padStart(2, "0");
    return `${hours}h${minutes}`;
  }
  if (typeof value === "string") {
    const match = value.match(/^(\d{1,2}):(\d{2})/);
    if (match) {
      const [, hh, mm] = match;
      return `${String(Number(hh)).padStart(2, "0")}h${mm}`;
    }
  }
  return "";
};

const formatDateTimeValue = (value) => {
  if (!value) return "";
  const parsed = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return "";
  }
  const datePart = formatDateValue(parsed);
  const timePart = formatTimeValue(parsed);
  return timePart ? `${datePart}-${timePart}` : datePart;
};

const formatListValue = (field, rawValue) => {
  if (!rawValue) return "";
  const options = Array.isArray(field?.options) ? field.options : [];
  if (Array.isArray(rawValue)) {
    const labels = rawValue
      .map((entry) => {
        if (typeof entry === "string") {
          const option = options.find((opt) => opt === entry);
          return option || entry;
        }
        if (typeof entry === "number") {
          const option = options.find((opt) => opt === entry);
          return option || String(entry);
        }
        if (entry && typeof entry === "object") {
          if (entry.label) return entry.label;
          if (entry.value) return entry.value;
          if (entry.name) return entry.name;
        }
        return "";
      })
      .filter((text) => typeof text === "string" && text.trim().length > 0);
    return labels.join("-");
  }
  if (typeof rawValue === "string" || typeof rawValue === "number") {
    const option = options.find((opt) => opt === rawValue);
    return option ? String(option) : String(rawValue);
  }
  if (rawValue && typeof rawValue === "object") {
    if (rawValue.label) return String(rawValue.label);
    if (rawValue.value) return String(rawValue.value);
    if (rawValue.name) return String(rawValue.name);
  }
  return "";
};

const formatDynamicSelectionValue = (rawValue) => {
  if (!rawValue || typeof rawValue !== "object") return "";
  const options = Array.isArray(rawValue.options) ? rawValue.options : [];
  const selected = new Set(
    Array.isArray(rawValue.selected)
      ? rawValue.selected.map((value) => String(value))
      : [],
  );
  if (selected.size === 0) return "";
  const labels = options
    .map((option) => {
      if (!option) return null;
      const id = option.id ? String(option.id) : null;
      if (id && selected.has(id)) {
        return option.label ?? option.name ?? option.value ?? null;
      }
      return null;
    })
    .filter((value) => typeof value === "string" && value.trim().length > 0);
  return labels.join("-");
};

const formatGenericValue = (value) => {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value.trim();
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  if (value instanceof Date) return formatDateTimeValue(value);
  if (Array.isArray(value)) {
    return value.map((entry) => formatGenericValue(entry)).filter(Boolean).join("-");
  }
  if (typeof value === "object") {
    if (value.label) return formatGenericValue(value.label);
    if (value.name) return formatGenericValue(value.name);
    if (value.value) return formatGenericValue(value.value);
    if (value.title) return formatGenericValue(value.title);
    if (value.files && Array.isArray(value.files)) {
      return value.files
        .map((file) => {
          if (!file) return "";
          if (typeof file === "string") return file;
          if (file.name) return file.name;
          if (file.label) return file.label;
          return "";
        })
        .filter(Boolean)
        .join("-");
    }
  }
  return "";
};

const formatValueForField = (field, value) => {
  if (!field) return formatGenericValue(value);
  switch (field.type) {
    case "date":
      return formatDateValue(value);
    case "time":
      return formatTimeValue(value);
    case "datetime":
    case "datetime-local":
      return formatDateTimeValue(value);
    case "list":
      return formatListValue(field, value);
    case "dynamic-select":
      return formatDynamicSelectionValue(value);
    case "decimal":
    case "number":
      return value === null || value === undefined ? "" : String(value);
    case "cpf":
    case "cnpj":
    case "cep":
    case "phone":
      return value ? String(value) : "";
    default:
      return formatGenericValue(value);
  }
};

const sanitizeFinalName = (name) => {
  if (!name) return "";
  const cleaned = name
    .replace(/[\r\n\t]+/g, " ")
    .replace(INVALID_FILENAME_CHARS, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (!cleaned) return "";
  if (cleaned.length > MAX_FILENAME_LENGTH) {
    return cleaned.slice(0, MAX_FILENAME_LENGTH).trim();
  }
  return cleaned;
};

export const buildAttachmentFileName = (
  template,
  {
    values = {},
    fields = [],
    extras = {},
    originalFileName,
  } = {},
) => {
  if (!template || typeof template !== "string") {
    return null;
  }
  const trimmedTemplate = template.trim();
  if (!trimmedTemplate) {
    return null;
  }

  const fieldMap = new Map();
  fields.forEach((field) => {
    if (field && field.id) {
      fieldMap.set(String(field.id), field);
    }
  });

  const tokenRegex = /\{\{\s*([^}]+?)\s*\}\}/g;
  const replaced = trimmedTemplate.replace(tokenRegex, (_, token) => {
    const key = String(token || "").trim();
    if (!key) return "";
    if (extras && Object.prototype.hasOwnProperty.call(extras, key)) {
      return formatGenericValue(extras[key]);
    }
    if (key === "data_atual") {
      return formatDateValue(new Date());
    }
    if (key === "hora_atual") {
      return formatTimeValue(new Date());
    }
    const field = fieldMap.get(key);
    if (field) {
      return formatValueForField(field, values[field.id]);
    }
    if (Object.prototype.hasOwnProperty.call(values, key)) {
      return formatGenericValue(values[key]);
    }
    return "";
  });

  const sanitized = sanitizeFinalName(replaced);
  if (!sanitized) {
    return null;
  }

  const { base: originalBase, extension: originalExtension } = splitFileName(
    originalFileName || "",
  );
  const safeBase = sanitizeBaseName(sanitized, sanitizeBaseName(originalBase));
  const safeExtension = sanitizeExtension(originalExtension);

  const hasExtensionInTemplate = /\.([a-zA-Z0-9]{1,12})$/.test(sanitized);
  if (hasExtensionInTemplate) {
    const { base, extension } = splitFileName(sanitized);
    const safeExtFromTemplate = sanitizeExtension(extension);
    const finalBase = sanitizeBaseName(base, safeBase);
    if (safeExtFromTemplate) {
      return `${finalBase}.${safeExtFromTemplate}`;
    }
    return finalBase;
  }

  if (safeExtension) {
    return `${safeBase}.${safeExtension}`;
  }
  return safeBase;
};

export const applyAttachmentTemplate = (file, template, context = {}) => {
  if (!file || !template) {
    return { file, generatedName: null };
  }
  const generatedName = buildAttachmentFileName(template, {
    ...context,
    originalFileName: context.originalFileName || file.name,
  });
  if (!generatedName || generatedName === file.name) {
    return { file, generatedName: generatedName || null };
  }
  try {
    const renamed = new File([file], generatedName, {
      type: file.type,
      lastModified: file.lastModified,
    });
    return { file: renamed, generatedName };
  } catch (error) {
    console.warn("Não foi possível aplicar nome personalizado ao arquivo", error);
    return { file, generatedName };
  }
};

export const collectTemplateFields = (fields = [], currentFieldId) =>
  fields
    .filter((field) => field && field.id && field.id !== currentFieldId)
    .map((field) => ({
      id: field.id,
      label: field.label || field.id,
      type: field.type,
    }));

export const getSampleValueForField = (field) => {
  if (!field) return "";
  switch (field.type) {
    case "date":
      return "2025-12-15";
    case "time":
      return "14:30";
    case "datetime":
    case "datetime-local":
      return "2025-12-15T14:30";
    case "decimal":
      return "1234,56";
    case "number":
      return 42;
    case "cpf":
      return "123.456.789-09";
    case "cnpj":
      return "12.345.678/0001-99";
    case "cep":
      return "12345-678";
    case "phone":
      return "11912345678";
    case "list":
      return Array.isArray(field.options) && field.options.length > 0
        ? field.options[0]
        : field.label || field.id;
    case "dynamic-select":
      return {
        options: [
          { id: "opt-1", label: field.options?.[0] || field.label || "Opção" },
        ],
        selected: ["opt-1"],
      };
    default:
      return field.label || field.id || "valor";
  }
};
